import { Link } from "react-router-dom";
import { Phone, ArrowRight, Heart, Handshake, House, Users, Clock, ShieldCheck, MapPin } from "@phosphor-icons/react";

const About = () => {
  const whoWeHelp = [
    "Families handling a loved one's estate",
    "Executors and administrators",
    "People downsizing to smaller homes",
    "Seniors moving to assisted living",
    "Anyone needing to liquidate household contents"
  ];

  const whatMakesUsDifferent = [
    {
      icon: Heart,
      title: "Compassionate Approach",
      description: "We understand this is often a difficult time. Our team treats every family with empathy and respect."
    },
    {
      icon: House,
      title: "No Need to Clean First",
      description: "You don't need to sort, organize, or clean before we arrive. We handle homes exactly as they are."
    },
    {
      icon: Handshake,
      title: "Full-Service Support",
      description: "From sorting and pricing to hosting the sale and cleanup, we manage every detail so you don't have to."
    },
    {
      icon: MapPin,
      title: "Local Expertise",
      description: "We know the Fargo-Moorhead market and price items appropriately for our regional buyers."
    }
  ];

  const communities = [
    "Fargo, ND", "Moorhead, MN", "West Fargo, ND", "Horace, ND", "Casselton, ND",
    "Dilworth, MN", "Barnesville, MN", "Hawley, MN", "Glyndon, MN",
    "Wahpeton, ND", "Breckenridge, MN", "Detroit Lakes, MN", "Pelican Rapids, MN", "Fergus Falls, MN"
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
                About Legacy Liquidators LLC - Fargo Estate Sale Company
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-[#1C1914] leading-tight mb-6">
                Professional Estate Sale Services in Fargo-Moorhead
              </h1>
              <p className="text-lg text-[#5C564D] leading-relaxed mb-8">
                Kathleen (Kathy) Schultz, owner of Legacy Liquidators LLC, has been helping families across the Fargo-Moorhead region handle estate sales with compassion, professionalism, and care for the past 9 years. We're the trusted estate sale company serving Fargo ND, Moorhead MN, West Fargo, Detroit Lakes, Fergus Falls, and surrounding communities.
              </p>
              <p className="text-lg text-[#5C564D] leading-relaxed mb-8">
                We understand that every item in a home carries memories. Whether you're dealing with a loved one's estate, downsizing after retirement, or managing a transition to assisted living, we're here to make the process easier. No need to clean first—we do the work for you!
              </p>
              <a
                href="tel:2182982420"
                className="inline-flex items-center gap-2 bg-[#2C4C3B] hover:bg-[#1E3629] text-white px-8 py-4 rounded-md transition-all duration-300 font-medium"
                data-testid="about-hero-phone"
              >
                <Phone weight="fill" className="w-5 h-5" />
                Call 218-298-2420
              </a>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_legacy-liquidators/artifacts/1pxk21so_f781b3b8-f316-4c7f-89f8-b8ba4a3df6e3.png"
                alt="Legacy Liquidators LLC estate sale display in Fargo ND - professional estate sale company serving Fargo, Moorhead, West Fargo, Detroit Lakes, Fergus Falls with in-home estate sales, estate liquidation, and downsizing services"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who We Help Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
                Who We Serve
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#1C1914] mb-6">
                Who We Help
              </h2>
              <p className="text-lg text-[#5C564D] leading-relaxed mb-8">
                Legacy Liquidators LLC serves families and individuals facing a variety of life situations that require professional estate sale services.
              </p>
              <ul className="space-y-4">
                {whoWeHelp.map((item, index) => (
                  <li key={index} className="flex items-center gap-3" data-testid={`who-we-help-${index}`}>
                    <div className="w-6 h-6 bg-[#2C4C3B] rounded-full flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#1C1914] font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#F7F5F0] p-8 md:p-12 rounded-lg">
              <h3 className="text-2xl font-serif font-medium text-[#1C1914] mb-6">Our Promise to You</h3>
              <p className="text-[#5C564D] leading-relaxed mb-6">
                When you work with Legacy Liquidators LLC, you're not just hiring an estate sale company. You're partnering with a team that genuinely cares about making your transition smoother.
              </p>
              <p className="text-[#5C564D] leading-relaxed mb-6">
                We treat every home—and every family—with the respect and compassion they deserve. We know this can be an emotional time, and we're here to support you every step of the way.
              </p>
              <p className="text-[#1C1914] font-semibold italic font-serif text-lg">
                "No need to clean first. We handle the work so you don't have to."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
              Why Choose Us
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#1C1914] mb-6">
              What Makes Us Different
            </h2>
            <p className="text-lg text-[#5C564D] max-w-2xl mx-auto">
              We've built our reputation on compassionate service, professional expertise, and a commitment to making estate sales stress-free.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whatMakesUsDifferent.map((item, index) => (
              <div
                key={index}
                className="bg-white border border-[#E2DCD0] p-8 md:p-10 rounded-lg card-hover"
                data-testid={`difference-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#2C4C3B]/10 rounded-lg flex items-center justify-center mb-6">
                  <item.icon weight="duotone" className="w-7 h-7 text-[#2C4C3B]" />
                </div>
                <h3 className="text-xl font-serif font-medium text-[#1C1914] mb-3">{item.title}</h3>
                <p className="text-[#5C564D] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#2C4C3B]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
              How We Work
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-white mb-6">
              Our Process
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Initial Consultation", desc: "We visit your home, assess the contents, and discuss your timeline and goals." },
              { step: "2", title: "Sorting & Pricing", desc: "Our team sorts through everything, researches values, and prices items appropriately." },
              { step: "3", title: "Staging & Marketing", desc: "We stage the home attractively and advertise to our network of buyers." },
              { step: "4", title: "Sale & Settlement", desc: "We manage the sale, handle all transactions, and provide detailed accounting." }
            ].map((item, index) => (
              <div key={index} className="text-center" data-testid={`process-${index}`}>
                <div className="w-16 h-16 bg-[#C5A059] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-[#1C1914] text-2xl font-serif font-bold">{item.step}</span>
                </div>
                <h3 className="text-xl font-serif font-medium text-white mb-3">{item.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Communities We Serve */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
              Service Area
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#1C1914] mb-6">
              Local Communities We Serve
            </h2>
            <p className="text-lg text-[#5C564D] max-w-2xl mx-auto">
              We serve families throughout Fargo-Moorhead and surrounding communities within about 60 miles.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {communities.map((community, index) => (
              <span
                key={index}
                className="bg-[#F7F5F0] border border-[#E2DCD0] px-4 py-2 rounded-full text-[#5C564D] text-sm"
              >
                {community}
              </span>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/service-areas"
              className="inline-flex items-center gap-2 text-[#2C4C3B] hover:text-[#1E3629] font-medium transition-colors"
              data-testid="about-view-areas"
            >
              View All Service Areas
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#1C1914] mb-6">
            Let Us Help Your Family
          </h2>
          <p className="text-lg text-[#5C564D] mb-8">
            Contact us today for a free, no-obligation consultation. We're here to answer your questions and help you through this transition.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:2182982420"
              className="inline-flex items-center justify-center gap-2 bg-[#2C4C3B] hover:bg-[#1E3629] text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="about-cta-phone"
            >
              <Phone weight="fill" className="w-5 h-5" />
              Call 218-298-2420
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="about-cta-contact"
            >
              Request a Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
